import {
  sliceEvents,
  EventRenderRange,
  createPlugin,
} from "@fullcalendar/core/index.js";
import { ViewProps, CalendarContentProps } from "@fullcalendar/core/internal";
import classNames from "classnames";
import dayjs from "dayjs";
import { ComponentProps, FC, memo } from "react";
import { EVENT_TYPES } from "../../../enums/event";
import { CustomBadge } from "../../../shared/CustomBadge/CustomBadge";
import Typhography from "../../../shared/Typhography/Typhography";
import style from "./CusromFlexListView.module.scss";
import { deepEqual } from "fast-equals";

const Event: FC<{
  dateStr: string;
  className: string;
  events: EventRenderRange[];
  dateClick: (arg: any) => void;
  date: any;
}> = memo(
  ({ date, dateStr, className, events, dateClick }) => {
    const d = dayjs(date);
    return (
      <div
        key={dateStr}
        className={classNames(style.containerDayList, className)}
        onClick={() => {
          dateClick({ date });
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <Typhography size="medium-14" className={style.titleList}>
            {d.format("DD")}
          </Typhography>
          <Typhography size="medium-14" className={style.titleList}>
            {d.format("ddd")}
          </Typhography>
        </div>
        <div className="d-flex flex-column gap-1 mt-1">
          {events.map((seg) => {
            const completed = seg.def.extendedProps.completed;
            return (
              <div
                className={classNames(style.eventRow, {
                  [style.eventRowCompleted]: completed,
                })}
                key={seg.def.defId}
              >
                {completed && <div className={style.completedDot} />}
                <CustomBadge
                  color={
                    seg.def.extendedProps.type === EVENT_TYPES.WORKOUT
                      ? "light-blue-dust"
                      : seg.def.extendedProps.type === EVENT_TYPES.TEST
                      ? "lightgreen"
                      : "lightblue"
                  }
                  label={seg.def.title}
                  className={style.containerBadge}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  },
  (prevProps, currProps) => {
    return (
      prevProps.className === currProps.className &&
      deepEqual(prevProps.events, currProps.events) &&
      prevProps.dateStr === currProps.dateStr
    );
  }
);

export const CustomFlexListView = (props: ComponentProps<any>) => {
  const segs = sliceEvents(props, true); // prendi tutti gli eventi allDay / in range

  const eventsByDate: Record<string, EventRenderRange[]> = {};

  segs.forEach((seg) => {
    const dateStr = seg.range.start.toISOString().split("T")[0];
    if (!eventsByDate[dateStr]) eventsByDate[dateStr] = [];
    eventsByDate[dateStr].push(seg);
  });

  const start = props.dateProfile.currentRange.start;
  const end = props.dateProfile.currentRange.end;
  const dates = [];
  const current = new Date(start);

  while (current < end) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        padding: "1rem",
      }}
    >
      {dates.map((date) => {
        const dateStr = date.toISOString().split("T")[0];
        const events = eventsByDate[dateStr] || [];
        const className = props.dayCellClassNames({ date });
        return (
          <Event
            key={dateStr}
            date={date}
            dateStr={dateStr}
            events={events}
            dateClick={props.dateClick}
            className={className}
          />
        );
      })}
    </div>
  );
};

class AddCalendarToProps {
  transform(viewProps: ViewProps, calendarProps: CalendarContentProps) {
    return {
      ...viewProps,
      dayCellClassNames:
        calendarProps.calendarApi.getOption("dayCellClassNames"),
      dateClick: calendarProps.calendarApi.getOption("dateClick"),
      eventClick: calendarProps.calendarApi.getOption("eventClick"),
    };
  }
}

export const customPlugin = createPlugin({
  name: "customPlugin",
  views: {
    duration: { days: 7 }, // 👈 mostra 7 giorni
    custom: {
      type: "CustomFlexListView",
      duration: { days: 7 },
      buttonText: "Settimana Lista",
      component: CustomFlexListView, // 👈 Usa direttamente il componente React
    },
  },
  viewPropsTransformers: [AddCalendarToProps],
});
